import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/hooks/useSeo";
import manifesto from "@/data/manifesto.json";

export default function ManifestoPage() {
  useSeo({
    title: manifesto.seo.title,
    description: manifesto.seo.description,
    path: "/noise-is-not-luxury",
    type: "article",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Noise Is Not Luxury",
        description: manifesto.seo.jsonld_description,
        author: { "@type": "Organization", name: "FMF" },
        publisher: { "@type": "Organization", name: "FMF" },
        mainEntityOfPage: `${process.env.REACT_APP_SITE_URL || ""}/noise-is-not-luxury`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "FMF", item: process.env.REACT_APP_SITE_URL },
          {
            "@type": "ListItem",
            position: 2,
            name: "Perspectives",
            item: `${process.env.REACT_APP_SITE_URL}/perspectives`,
          },
          { "@type": "ListItem", position: 3, name: "Noise Is Not Luxury" },
        ],
      },
    ],
  });

  return (
    <main data-testid="manifesto-page" className="relative min-h-screen">
      <Link
        data-testid="manifesto-home-link"
        to="/"
        className="absolute top-8 left-6 md:left-14 z-10"
      >
        <img src="/fmf-logo-white.png" alt="FMF" className="w-14 opacity-90" />
      </Link>
      <section className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <Reveal>
          <p className="font-mono-fmf text-[13px] uppercase tracking-[0.5em] text-[var(--dim)]">
            {manifesto.kicker}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <h1
            data-testid="manifesto-title"
            className="mt-12 font-editorial font-light text-5xl md:text-7xl text-white leading-tight"
          >
            {manifesto.title}
          </h1>
        </Reveal>
        <Reveal delay={0.6}>
          <p
            data-testid="manifesto-intro-placeholder"
            className="mt-[10vh] max-w-xl mx-auto font-mono-fmf text-[13px] tracking-[0.25em] uppercase text-[var(--dim)]"
          >
            {manifesto.intro}
          </p>
        </Reveal>
      </section>

      {manifesto.stages.map((stage, i) => (
        <section
          key={stage.name}
          data-testid={`manifesto-stage-${i}`}
          className="min-h-[80vh] flex flex-col items-center justify-center px-6 text-center"
        >
          <Reveal>
            <p className="font-mono-fmf text-[12px] tracking-[0.6em] text-[var(--dim)]">
              {String(i + 1).padStart(2, "0")}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <h2 className="mt-8 font-editorial font-light text-4xl md:text-6xl tracking-[0.08em] text-white uppercase">
              {stage.name}
            </h2>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="mt-12 max-w-xl mx-auto font-mono-fmf text-[13px] tracking-[0.25em] uppercase text-[var(--dim)]">
              {stage.text}
            </p>
          </Reveal>
          {i < manifesto.stages.length - 1 && (
            <Reveal delay={0.6}>
              <p className="mt-[12vh] font-editorial font-light text-3xl text-[var(--dim)]">↓</p>
            </Reveal>
          )}
        </section>
      ))}

      <section className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <Reveal>
          <p
            data-testid="manifesto-closing-placeholder"
            className="max-w-xl mx-auto font-mono-fmf text-[13px] tracking-[0.25em] uppercase text-[var(--dim)]"
          >
            {manifesto.closing}
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <Link
            data-testid="manifesto-back"
            to="/perspectives"
            className="mt-[10vh] inline-block font-mono-fmf text-[12px] uppercase tracking-[0.4em] text-[var(--dim)] hover:text-white transition-colors duration-700"
          >
            Perspectives
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
